import { Search } from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';

import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import {
  useListAgentChatThreadsQuery,
  useListAgentChatWorkspacesQuery,
} from '@/services/api/agentChatService';
import { useListDemandExamplesQuery } from '@/services/api/demandExampleService';
import {
  type DemandExampleTemplate,
  useListTemplatesQuery,
} from '@/services/api/demandExampleTemplateService';
import type { DemandExample } from '@/services/types/demand-example-types';

interface TemplatePickerDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: (
    templateId: string,
    opts?: {
      workspaceId?: string;
      threadId?: string;
      runProofreading?: boolean;
    },
  ) => void;
  confirmLabel?: string;
  matterId?: string;
  workspaceId?: string;
  currentThreadId?: string;
}

/** Flattened template with example context for display */
interface FlattenedTemplate {
  template: DemandExampleTemplate;
  example: DemandExample;
  displayLabel: string;
  /** Full context (firm + type) used for sorting and search filtering */
  contextLabel: string;
  /** Short subtitle shown in the UI (type only, since firm is in template name) */
  subtitleLabel: string;
}

/** Helper component to fetch templates for a single example */
const ExampleTemplatesFetcher = ({
  example,
  onTemplatesLoaded,
}: {
  example: DemandExample;
  onTemplatesLoaded: (
    exampleId: string,
    templates: DemandExampleTemplate[],
  ) => void;
}) => {
  const { data: templates = [] } = useListTemplatesQuery({
    demandExampleId: example.id,
  });

  useEffect(() => {
    onTemplatesLoaded(example.id, templates);
  }, [example.id, templates, onTemplatesLoaded]);

  return null; // This component just fetches data
};

/**
 * Simplified template picker dialog - shows all templates in a single searchable list.
 * Each template shows its name prominently with firm/type context.
 */
export const TemplatePickerDialog = ({
  open,
  onOpenChange,
  onConfirm,
  confirmLabel = 'Confirm',
  matterId,
  workspaceId,
  currentThreadId,
}: TemplatePickerDialogProps) => {
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(
    null,
  );
  const [selectedThreadId, setSelectedThreadId] = useState<string>('system');
  const [runProofreading, setRunProofreading] = useState<boolean>(true);
  const [searchQuery, setSearchQuery] = useState<string>('');

  // Store fetched templates by example ID
  const [templatesByExample, setTemplatesByExample] = useState<
    Record<string, DemandExampleTemplate[]>
  >({});

  // Fetch all demand examples
  const { data: demandExamplesResp, isLoading: isLoadingExamples } =
    useListDemandExamplesQuery(
      { ordering: '-created_at', limit: 100, offset: 0 },
      { skip: !open },
    );
  const demandExamples = demandExamplesResp?.results ?? [];

  // Callback for when templates are loaded for an example
  const handleTemplatesLoaded = React.useCallback(
    (exampleId: string, templates: DemandExampleTemplate[]) => {
      setTemplatesByExample((prev) => {
        if (
          prev[exampleId] &&
          JSON.stringify(prev[exampleId]) === JSON.stringify(templates)
        ) {
          return prev; // No change
        }
        return { ...prev, [exampleId]: templates };
      });
    },
    [],
  );

  // Flatten all templates with their example context
  const flattenedTemplates: FlattenedTemplate[] = useMemo(() => {
    const result: FlattenedTemplate[] = [];

    demandExamples.forEach((example) => {
      const templates = templatesByExample[example.id] ?? [];
      const firmName = example.firm_details?.name;
      const matterType = example.matter_type;

      // Build context: "Firm Name - Type" or just one of them
      const contextParts: string[] = [];
      if (firmName) contextParts.push(firmName);
      if (matterType) contextParts.push(matterType);
      const contextLabel = contextParts.join(' - ') || example.title || '';

      templates.forEach((template) => {
        result.push({
          template,
          example,
          displayLabel: template.name,
          contextLabel,
          subtitleLabel: matterType || example.title || '',
        });
      });
    });

    // Sort by context (firm-type) then by template name
    return result.sort((a, b) => {
      const contextCmp = a.contextLabel.localeCompare(b.contextLabel);
      if (contextCmp !== 0) return contextCmp;
      return a.displayLabel.localeCompare(b.displayLabel);
    });
  }, [demandExamples, templatesByExample]);

  // Filter templates by search query
  const filteredTemplates = useMemo(() => {
    if (!searchQuery.trim()) return flattenedTemplates;
    const query = searchQuery.toLowerCase();
    return flattenedTemplates.filter((item) => {
      const templateName = item.displayLabel.toLowerCase();
      const context = item.contextLabel.toLowerCase();
      const exampleTitle = item.example.title?.toLowerCase() ?? '';
      return (
        templateName.includes(query) ||
        context.includes(query) ||
        exampleTitle.includes(query)
      );
    });
  }, [flattenedTemplates, searchQuery]);

  // Workspace list
  const { data: workspaces = [] } = useListAgentChatWorkspacesQuery(
    { matterId: matterId ?? '' },
    { skip: !open || !matterId },
  );

  const [selectedWorkspaceId, setSelectedWorkspaceId] = useState<string>(
    workspaceId ?? 'system',
  );

  const { data: threads = [] } = useListAgentChatThreadsQuery(
    {
      matterId: matterId ?? '',
      workspaceId:
        selectedWorkspaceId !== 'system' ? selectedWorkspaceId : undefined,
    },
    { skip: !open || !matterId },
  );

  // Reset state when dialog opens
  useEffect(() => {
    if (open) {
      setSelectedThreadId(currentThreadId ?? 'system');
      setSelectedWorkspaceId(workspaceId ?? 'system');
      setSelectedTemplateId(null);
      setSearchQuery('');
      setTemplatesByExample({});
    }
  }, [open, currentThreadId, workspaceId]);

  const handleConfirm = () => {
    if (!selectedTemplateId) return;
    onConfirm(selectedTemplateId, {
      workspaceId:
        selectedWorkspaceId === 'system' ? undefined : selectedWorkspaceId,
      threadId: selectedThreadId === 'system' ? undefined : selectedThreadId,
      runProofreading,
    });
  };

  const isLoadingTemplates =
    demandExamples.length > 0 &&
    Object.keys(templatesByExample).length < demandExamples.length;
  const isLoading = isLoadingExamples || isLoadingTemplates;

  const renderTemplateList = () => {
    if (isLoadingExamples) {
      return <p className="p-4 text-muted-foreground">Loading templates…</p>;
    }

    if (demandExamples.length === 0) {
      return (
        <p className="p-4 text-muted-foreground">No templates available.</p>
      );
    }

    if (flattenedTemplates.length === 0 && !isLoadingTemplates) {
      return (
        <p className="p-4 text-muted-foreground">No templates available.</p>
      );
    }

    if (filteredTemplates.length === 0 && flattenedTemplates.length > 0) {
      return (
        <p className="p-4 text-muted-foreground">
          No templates match your search.
        </p>
      );
    }

    return (
      <div className="divide-y">
        {filteredTemplates.map((item) => {
          const isSelected = selectedTemplateId === item.template.id;
          const sectionsCount = item.template.sections_count;
          return (
            <label
              key={item.template.id}
              htmlFor={`template-${item.template.id}`}
              className={`flex items-start gap-2.5 px-3 py-2.5 cursor-pointer hover:bg-muted/50 ${
                isSelected ? 'bg-blue-50 dark:bg-blue-900/20' : ''
              }`}
            >
              <input
                id={`template-${item.template.id}`}
                type="radio"
                name="demandTemplate"
                value={item.template.id}
                checked={isSelected}
                onChange={() => setSelectedTemplateId(item.template.id)}
                className="mt-0.5 flex-shrink-0"
              />
              <div className="min-w-0">
                <p className="text-sm font-medium">{item.displayLabel}</p>
                <p className="text-sm text-muted-foreground">
                  {item.subtitleLabel || '—'}
                  {sectionsCount != null && (
                    <span>
                      {' '}
                      &middot; {sectionsCount}{' '}
                      {sectionsCount === 1 ? 'section' : 'sections'}
                    </span>
                  )}
                </p>
              </div>
            </label>
          );
        })}
      </div>
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-3xl w-[90vw] max-h-[85vh] flex flex-col overflow-hidden">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle>Select Demand Template</DialogTitle>
        </DialogHeader>

        {/* Hidden fetchers for each example's templates */}
        {open &&
          demandExamples.map((example) => (
            <ExampleTemplatesFetcher
              key={example.id}
              example={example}
              onTemplatesLoaded={handleTemplatesLoaded}
            />
          ))}

        {/* Scrollable content area */}
        <div className="flex-1 overflow-y-auto min-h-0 space-y-4">
          {/* Template Selection */}
          <div>
            <div className="relative mb-2">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search templates by name, firm, or type (3P, UM, etc.)..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-8"
                autoFocus
              />
            </div>
            <div className="max-h-[35vh] overflow-y-auto border rounded">
              {renderTemplateList()}
            </div>
            {!isLoading && flattenedTemplates.length > 0 && (
              <p className="text-xs text-muted-foreground mt-1">
                {filteredTemplates.length} of {flattenedTemplates.length}{' '}
                templates
              </p>
            )}
          </div>

          {/* Workspace selection */}
          {matterId && (
            <div>
              <p className="font-semibold mb-1 text-sm">Workspace</p>
              <div className="space-y-2 max-h-32 overflow-y-auto border p-2 rounded">
                <label
                  className="flex items-center space-x-2 cursor-pointer text-sm"
                  htmlFor="ws-system"
                >
                  <input
                    id="ws-system"
                    type="radio"
                    name="targetWorkspace"
                    value="system"
                    checked={selectedWorkspaceId === 'system'}
                    onChange={() => setSelectedWorkspaceId('system')}
                  />
                  <span>System Workspace (auto)</span>
                </label>
                {workspaces.map((ws) => {
                  const radioId = `ws-${ws.id}`;
                  return (
                    <label
                      key={ws.id}
                      htmlFor={radioId}
                      className="flex items-center space-x-2 cursor-pointer text-sm"
                    >
                      <input
                        id={radioId}
                        type="radio"
                        name="targetWorkspace"
                        value={ws.id}
                        checked={selectedWorkspaceId === ws.id}
                        onChange={() => setSelectedWorkspaceId(ws.id)}
                      />
                      <span>{ws.name || 'Workspace'}</span>
                    </label>
                  );
                })}
              </div>
            </div>
          )}

          {/* Thread selection */}
          {matterId && (
            <div>
              <p className="font-semibold mb-1 text-sm">Target Thread</p>
              <div className="space-y-2 max-h-32 overflow-y-auto border p-2 rounded">
                <label
                  className="flex items-center space-x-2 cursor-pointer text-sm"
                  htmlFor="th-system"
                >
                  <input
                    id="th-system"
                    type="radio"
                    name="targetThread"
                    value="system"
                    checked={selectedThreadId === 'system'}
                    onChange={() => setSelectedThreadId('system')}
                  />
                  <span>System Thread (auto)</span>
                </label>
                {threads.map((th) => {
                  const radioId = `th-${th.id}`;
                  return (
                    <label
                      key={th.id}
                      htmlFor={radioId}
                      className="flex items-center space-x-2 cursor-pointer text-sm"
                    >
                      <input
                        id={radioId}
                        type="radio"
                        name="targetThread"
                        value={th.id}
                        checked={selectedThreadId === th.id}
                        onChange={() => setSelectedThreadId(th.id)}
                      />
                      <span>{th.title || 'Untitled Thread'}</span>
                    </label>
                  );
                })}
              </div>
            </div>
          )}

          {/* Proofreading Option */}
          <div className="p-3 bg-muted/50 rounded-lg">
            <div className="flex items-start space-x-2">
              <Checkbox
                id="proofreading-checkbox"
                checked={runProofreading}
                onCheckedChange={(checked) =>
                  setRunProofreading(checked as boolean)
                }
                className="mt-0.5"
              />
              <div className="flex-1">
                <span className="text-sm font-medium leading-none">
                  Run proofreading and revision
                </span>
                <p className="text-xs text-muted-foreground mt-1">
                  AI will proofread each section and apply revisions based on
                  legal writing best practices.
                </p>
              </div>
            </div>
          </div>
        </div>

        <DialogFooter className="flex-shrink-0 mt-4">
          <Button variant="secondary" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button disabled={!selectedTemplateId} onClick={handleConfirm}>
            {confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
